import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  ArrowRight, 
  Check, 
  Clock, 
  Users, 
  TrendingDown, 
  AlertCircle,
  DollarSign,
  Target,
  Zap,
  Battery,
  TrendingUp,
  Shield,
  Briefcase,
  PiggyBank,
  BarChart3,
  X,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/4cBuBGX5rHUXw5076Ap3";

const FOUNDERS_IMG = "https://customer-assets.emergentagent.com/job_ac335dbd-d033-4455-9009-be3eb6ed9619/artifacts/i9km8emq_Deux_fondateurs_Cledici_202603211253.jpeg";
const TEAM_IMG = "https://customer-assets.emergentagent.com/job_ac335dbd-d033-4455-9009-be3eb6ed9619/artifacts/017wipi6_%C3%89quipe_fin_s%C3%A9minaire_202603211253.jpeg";

// ============ SOCIAL PROOF BAR ============
const SocialProofBar = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissed) return;
    const timer = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(timer);
  }, [dismissed]);

  if (!visible || dismissed) return null;

  const count = 8 + Math.floor(Math.random() * 7);

  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 80, opacity: 0 }}
      className="fixed bottom-20 md:bottom-6 left-4 z-40 bg-[#111111] border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3 shadow-2xl max-w-xs"
      data-testid="social-proof-bar"
    >
      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
      <p className="text-white text-xs">
        <Strong>{count} diagnostics</Strong> <span className="text-[#A1A1AA]">réalisés cette semaine</span>
      </p>
      <button onClick={() => setDismissed(true)} className="text-[#52525B] hover:text-white ml-1 flex-shrink-0">
        <X className="w-3 h-3" />
      </button>
    </motion.div>
  );
};

const Strong = ({ children }) => <span className="font-semibold">{children}</span>;

// ============ STICKY CTA MOBILE ============
const StickyCTAMobile = ({ onOpenForm }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-[#050505]/95 backdrop-blur-sm border-t border-white/10 px-4 py-3" data-testid="sticky-cta-mobile">
      <Button
        onClick={onOpenForm}
        className="w-full bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold py-3 rounded-lg text-sm"
        data-testid="sticky-cta-btn"
      >
        Diagnostic gratuit
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

// ============ AUTHORITY BLOCK (SHARED) ============
const AuthorityBlock = ({ onOpenForm }) => {
  const stats = [
    { value: "92%", label: "Rétention propriétaires" },
    { value: "270+", label: "Logements repris" },
    { value: "15+", label: "Personnes dans l'équipe" }
  ];

  const testimonials = [
    {
      quote: "J'ai travaillé 4 ans non-stop, 7j/7. Cledici a repris mes 32 logements en 6 semaines. Aujourd'hui je touche un revenu passif et j'ai retrouvé ma vie.",
      name: "Sophie M.",
      detail: "28 logements, Lyon",
      tag: "Revenu passif"
    },
    {
      quote: "Je pensais que ma conciergerie ne valait rien parce qu'elle était déficitaire. Gaël et Nicolas m'ont montré que mes contrats avaient de la valeur. Rachat bouclé en 45 jours.",
      name: "Marc D.",
      detail: "45 logements, Bordeaux",
      tag: "Rachat structuré"
    },
    {
      quote: "Le plus dur c'était l'idée d'abandonner mes propriétaires. La transition a été impeccable, tous sont restés. Je recommande les yeux fermés.",
      name: "Julien R.",
      detail: "18 logements, Nice",
      tag: "Rachat structuré"
    }
  ];

  return (
    <section className="py-20 px-4 bg-[#0A0A0A] relative overflow-hidden" data-testid="authority-block">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF4500]/[0.015] to-transparent" />
      <div className="max-w-6xl mx-auto relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#FF4500] text-sm tracking-[0.2em] uppercase font-medium">Qui sommes-nous</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-4">
            Des opérateurs, pas des consultants
          </h2>
          <p className="text-[#A1A1AA] text-base max-w-xl mx-auto">
            On ne donne pas de conseils. On rachète et on exploite des conciergeries. C'est notre métier au quotidien.
          </p>
        </motion.div>

        {/* Founders row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mb-16"
          data-testid="authority-founders"
        >
          <div className="w-full md:w-2/5 flex-shrink-0">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img src={FOUNDERS_IMG} alt="Gaël Pélerin et Nicolas Moreau, co-fondateurs de Cledici Exit" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 flex gap-6">
                <div>
                  <p className="text-white font-semibold text-sm">Gaël Pélerin</p>
                  <p className="text-white/50 text-xs">Co-fondateur</p>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Nicolas Moreau</p>
                  <p className="text-white/50 text-xs">Co-fondateur</p>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-3/5">
            <blockquote className="text-lg md:text-xl font-light text-white leading-relaxed mb-5" data-testid="authority-quote">
              "La majorité des conciergeries ne sont ni structurées pour être revendues, ni pour fonctionner sans leur fondateur. On a créé Cledici Exit pour changer ça."
            </blockquote>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-5">
              Anciens gérants de conciergerie nous-mêmes, on connaît le quotidien : les appels à 23h, les ménages ratés, les propriétaires exigeants. On a construit une structure capable de reprendre et d'améliorer n'importe quel portefeuille.
            </p>
            <div className="flex items-center gap-3">
              <div className="w-6 h-[2px] bg-[#FF4500]" />
              <span className="text-[#FF4500] text-sm font-medium">Gaël & Nicolas</span>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-3 gap-4 mb-16"
          data-testid="authority-stats"
        >
          {stats.map((s, i) => (
            <div key={i} className="text-center py-6 md:py-8 rounded-2xl bg-[#111111] border border-white/5" data-testid={`stat-${i}`}>
              <p className="text-2xl md:text-4xl font-bold text-[#FF4500] mb-1">{s.value}</p>
              <p className="text-[#A1A1AA] text-xs md:text-sm">{s.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mb-16"
          data-testid="authority-testimonials"
        >
          <h3 className="text-center text-base font-semibold text-white mb-8">Ils ont fait le pas</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="p-5 rounded-2xl bg-[#111111] border border-white/5 flex flex-col" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} className="w-3.5 h-3.5 text-[#FF4500]" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                  ))}
                </div>
                <p className="text-[#A1A1AA] text-sm leading-relaxed flex-1 mb-4">"{t.quote}"</p>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <div>
                    <p className="text-white font-medium text-sm">{t.name}</p>
                    <p className="text-[#52525B] text-xs">{t.detail}</p>
                  </div>
                  <span className="text-[9px] uppercase tracking-wider text-[#FF4500]/60 bg-[#FF4500]/5 px-2 py-1 rounded">{t.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Team photo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="relative rounded-2xl overflow-hidden"
          data-testid="authority-team"
        >
          <img src={TEAM_IMG} alt="L'équipe Cledici Exit au complet" className="w-full h-56 md:h-72 object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
            <p className="text-white text-base md:text-lg font-semibold mb-1">
              Une équipe de +15 opérationnels terrain
            </p>
            <p className="text-white/50 text-sm max-w-lg mx-auto">
              City managers, responsables ménage, revenue managers, support propriétaires — chaque reprise est portée par une équipe dédiée.
            </p>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

// ============ SHARED HEADER ============
const MainHeader = ({ variant = "neutral", onOpenForm }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#050505]/95 backdrop-blur-xl border-b border-white/5" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo-cledici-exit.png" alt="Cledici Exit" className="h-8" />
        </Link>
        <div className="flex items-center gap-3">
          <Link 
            to="/sortie" 
            className={`hidden md:block text-sm ${variant === 'sortie' ? 'text-[#FF4500]' : 'text-white/60 hover:text-white'} transition-colors`}
          >
            Sortie opérationnelle
          </Link>
          <Link 
            to="/valorisation" 
            className={`hidden md:block text-sm ${variant === 'valorisation' ? 'text-[#FF4500]' : 'text-white/60 hover:text-white'} transition-colors`}
          >
            Rachat & Revenu Passif
          </Link>
          <Button 
            onClick={onOpenForm}
            className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold px-4 md:px-6 py-2 rounded-lg"
            data-testid="header-diagnostic-btn"
          >
            Diagnostic gratuit
          </Button>
        </div>
      </div>
    </header>
  );
};

// ============ SHARED FOOTER ============
const MainFooter = () => (
  <footer className="bg-[#0A0A0A] border-t border-white/5 py-16 px-4" data-testid="main-footer">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
        <div>
          <Link to="/" className="flex items-center gap-2 mb-4">
            <img src="/logo-cledici-exit.png" alt="Cledici Exit" className="h-8" />
          </Link>
          <p className="text-sm text-[#71717A] leading-relaxed">
            Rachat et reprise de conciergeries Airbnb. Sortez intelligemment de votre activité.
          </p>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Votre situation</h4>
          <ul className="space-y-2">
            <li><Link to="/sortie" className="text-sm text-[#71717A] hover:text-white transition-colors">Sortie opérationnelle</Link></li>
            <li><Link to="/valorisation" className="text-sm text-[#71717A] hover:text-white transition-colors">Rachat & Revenu passif</Link></li>
            <li><a href="/conciergerie-airbnb-fatigue" className="text-sm text-[#71717A] hover:text-white transition-colors">Fatigue & épuisement</a></li>
            <li><a href="/conciergerie-airbnb-rentable" className="text-sm text-[#71717A] hover:text-white transition-colors">Rentabilité en question</a></li>
            <li><a href="/problemes-conciergerie-airbnb" className="text-sm text-[#71717A] hover:text-white transition-colors">Problèmes courants</a></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Solutions</h4>
          <ul className="space-y-2">
            <li><a href="/vendre-sa-conciergerie" className="text-sm text-[#71717A] hover:text-white transition-colors">Vendre sa conciergerie</a></li>
            <li><a href="/ceder-conciergerie" className="text-sm text-[#71717A] hover:text-white transition-colors">Céder sa conciergerie</a></li>
            <li><a href="/arreter-conciergerie-airbnb" className="text-sm text-[#71717A] hover:text-white transition-colors">Arrêter sa conciergerie</a></li>
            <li><a href="/sortir-operationnel-conciergerie" className="text-sm text-[#71717A] hover:text-white transition-colors">Sortir de l'opérationnel</a></li>
            <li><a href="/quitter-gestion-airbnb" className="text-sm text-[#71717A] hover:text-white transition-colors">Quitter la gestion Airbnb</a></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Informations</h4>
          <ul className="space-y-2">
            <li><a href="/blog" className="text-sm text-[#FF4500] hover:text-white transition-colors">Blog</a></li>
            <li><a href="/reprendre-conciergerie" className="text-sm text-[#71717A] hover:text-white transition-colors">Reprendre une conciergerie</a></li>
            <li><a href="/conciergerie-airbnb-ne-marche-pas" className="text-sm text-[#71717A] hover:text-white transition-colors">Conciergerie qui ne marche pas</a></li>
            <li><a href="#" className="text-sm text-[#71717A] hover:text-white transition-colors">Mentions légales</a></li>
          </ul>
        </div>
      </div>

      {/* Nos partenaires */}
      <div className="pt-8 border-t border-white/5 mb-8">
        <p className="text-xs text-[#52525B] uppercase tracking-wider mb-3">Nos partenaires</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <a href="https://www.cledici.fr" target="_blank" rel="noopener noreferrer" className="text-xs text-[#71717A] hover:text-[#FF4500] transition-colors">Cledici — Conciergerie</a>
          <a href="https://www.gt-bnb.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#71717A] hover:text-[#FF4500] transition-colors">GT-BNB — Système d'exploitation</a>
          <a href="https://www.hostprotect.io" target="_blank" rel="noopener noreferrer" className="text-xs text-[#71717A] hover:text-[#FF4500] transition-colors">HostProtect — Assurance biens</a>
          <a href="https://www.quozyli.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#71717A] hover:text-[#FF4500] transition-colors">Quozyli — Linge & équipement</a>
        </div>
      </div>

      <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-sm text-[#71717A]">© 2025 Cledici Exit. Tous droits réservés.</p>
        <p className="text-xs text-[#52525B]">Rachat de conciergeries Airbnb partout en France</p>
      </div>
    </div>
  </footer>
);

// ============ SEGMENTATION OVERLAY ============
const SegmentationOverlay = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 md:p-12 max-w-3xl w-full relative"
          onClick={e => e.stopPropagation()}
          data-testid="segmentation-overlay"
        >
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
            data-testid="overlay-close-btn"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>

          <div className="text-center mb-10">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="inline-block px-3 py-1 rounded-full bg-[#FF4500]/10 text-[#FF4500] text-xs font-medium tracking-wide uppercase mb-4">
                Orientez-vous
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Quelle est votre situation ?
              </h2>
              <p className="text-[#A1A1AA]">
                Choisissez le parcours qui vous correspond pour une expérience personnalisée.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              onClick={() => { onClose(); navigate('/sortie'); }}
              className="group p-6 bg-[#111111] border border-white/10 rounded-xl text-left hover:border-[#FF4500]/50 hover:bg-[#111111]/80 transition-all duration-300"
              data-testid="overlay-choice-sortie"
            >
              <div className="w-14 h-14 rounded-xl bg-red-500/10 flex items-center justify-center mb-4 group-hover:bg-red-500/20 transition-colors">
                <Battery className="w-7 h-7 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Je suis épuisé / bloqué
              </h3>
              <p className="text-[#A1A1AA] text-sm mb-4">
                Fatigue, charge mentale, opérationnel envahissant... Je veux sortir de cette situation.
              </p>
              <span className="inline-flex items-center gap-2 text-[#FF4500] font-medium text-sm group-hover:gap-3 transition-all">
                Sortir de l'opérationnel
                <ArrowRight className="w-4 h-4" />
              </span>
            </motion.button>

            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              onClick={() => { onClose(); navigate('/valorisation'); }}
              className="group p-6 bg-[#111111] border border-white/10 rounded-xl text-left hover:border-[#FF4500]/50 hover:bg-[#111111]/80 transition-all duration-300"
              data-testid="overlay-choice-valorisation"
            >
              <div className="w-14 h-14 rounded-xl bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 transition-colors">
                <TrendingUp className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Mon activité fonctionne
              </h3>
              <p className="text-[#A1A1AA] text-sm mb-4">
                Rentable et structurée, je veux la céder ou la transformer en revenus passifs.
              </p>
              <span className="inline-flex items-center gap-2 text-[#FF4500] font-medium text-sm group-hover:gap-3 transition-all">
                Recevoir une offre
                <ArrowRight className="w-4 h-4" />
              </span>
            </motion.button>
          </div>

          <p className="text-center text-[#71717A] text-xs mt-6">
            Vous pourrez toujours changer de parcours plus tard.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============ HOMEPAGE NEUTRE ============
export const NeutralHomePage = ({ onOpenForm }) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has seen overlay before
    const hasSeenOverlay = localStorage.getItem('cledici_seen_overlay');
    if (!hasSeenOverlay) {
      const timer = setTimeout(() => {
        setShowOverlay(true);
        localStorage.setItem('cledici_seen_overlay', 'true');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Cledici Exit | On rachète votre conciergerie Airbnb</title>
        <meta name="description" content="Rachat direct de conciergeries Airbnb. Paiement structuré (cash + earn-out) ou revenu passif sans effort. Offre sous 48h. Diagnostic gratuit en 3 minutes." />
      </Helmet>

      <MainHeader variant="neutral" onOpenForm={onOpenForm} />
      
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative pt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF4500]/5 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1757359315242-9813e0544bb7?w=1920')] bg-cover bg-center opacity-5" />
        
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center relative z-10 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-[#A1A1AA] mb-8">
              <Sparkles className="w-4 h-4 text-[#FF4500]" />
              Pour les gérants de conciergerie
            </span>
          </motion.div>

          <motion.h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="homepage-title"
          >
            Vous réfléchissez à <span className="text-[#FF4500]">arrêter</span> ou faire évoluer votre conciergerie ?
          </motion.h1>

          <motion.p 
            className="text-lg md:text-xl text-[#A1A1AA] mb-16 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Il existe plusieurs façons d'en sortir intelligemment, selon votre situation.
            <br className="hidden md:block" />
            Découvrez laquelle est faite pour vous.
          </motion.p>

          {/* Segmentation Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto"
            data-testid="segmentation-section"
          >
            {/* Choice 1: Sortie */}
            <button
              onClick={() => navigate('/sortie')}
              className="group relative p-8 bg-[#0A0A0A] border border-white/10 rounded-2xl text-left hover:border-[#FF4500]/50 transition-all duration-300 overflow-hidden"
              data-testid="choice-sortie"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-xl bg-red-500/10 flex items-center justify-center mb-6 group-hover:bg-red-500/20 transition-colors">
                  <Battery className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  Je suis épuisé / bloqué dans l'opérationnel
                </h3>
                <p className="text-[#A1A1AA] mb-6 leading-relaxed">
                  Fatigue permanente, charge mentale, impossible de décrocher... Vous voulez reprendre le contrôle de votre vie.
                </p>
                <span className="inline-flex items-center gap-2 text-[#FF4500] font-semibold group-hover:gap-3 transition-all">
                  Sortir de l'opérationnel
                  <ArrowRight className="w-5 h-5" />
                </span>
              </div>
            </button>

            {/* Choice 2: Rachat/Revenu Passif */}
            <button
              onClick={() => navigate('/valorisation')}
              className="group relative p-8 bg-[#0A0A0A] border border-white/10 rounded-2xl text-left hover:border-[#FF4500]/50 transition-all duration-300 overflow-hidden"
              data-testid="choice-valorisation"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-xl bg-green-500/10 flex items-center justify-center mb-6 group-hover:bg-green-500/20 transition-colors">
                  <TrendingUp className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  Mon activité fonctionne, je veux la céder
                </h3>
                <p className="text-[#A1A1AA] mb-6 leading-relaxed">
                  Rentable et structurée, vous souhaitez recevoir une offre de rachat ou transformer votre activité en revenus passifs à vie.
                </p>
                <span className="inline-flex items-center gap-2 text-[#FF4500] font-semibold group-hover:gap-3 transition-all">
                  Recevoir une offre
                  <ArrowRight className="w-5 h-5" />
                </span>
              </div>
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-[#71717A] text-sm mt-8"
          >
            Pas sûr de votre situation ? <button onClick={() => setShowOverlay(true)} className="text-[#FF4500] hover:underline">Laissez-nous vous guider</button>
          </motion.p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center pt-2"
          >
            <div className="w-1.5 h-3 rounded-full bg-white/40" />
          </motion.div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Une sortie intelligente, c'est quoi ?
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto mb-12">
              C'est préserver ce que vous avez construit tout en retrouvant votre liberté.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: "Sans tout perdre", desc: "Valorisez votre travail et vos mandats" },
              { icon: Clock, title: "À votre rythme", desc: "Une transition adaptée à votre timeline" },
              { icon: Target, title: "Accompagné", desc: "Un expert à vos côtés à chaque étape" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 bg-[#111111] border border-white/5 rounded-xl"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FF4500]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-[#FF4500]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <AuthorityBlock onOpenForm={onOpenForm} />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FF4500]/5 blur-3xl" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Pas sûr de la bonne direction ?
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-8">
            Notre diagnostic gratuit analyse votre situation et vous oriente vers la meilleure option.
          </p>
          <button 
            onClick={onOpenForm}
            className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold px-8 py-4 text-lg rounded-lg inline-flex items-center justify-center gap-2 transition-colors"
            data-testid="homepage-cta"
          >
            Démarrer mon diagnostic gratuit
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <MainFooter />

      <SocialProofBar />
      <StickyCTAMobile onOpenForm={onOpenForm} />

      {/* Segmentation Overlay */}
      <SegmentationOverlay isOpen={showOverlay} onClose={() => setShowOverlay(false)} />
    </div>
  );
};

// ============ LANDING 1: SORTIE / FATIGUE ============
export const SortieFatiguePage = ({ onOpenForm }) => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Sortie conciergerie Airbnb | Arrêter sans tout perdre | Cledici Exit</title>
        <meta name="description" content="Épuisé par votre conciergerie Airbnb ? On la rachète (cash + earn-out) ou on reprend l'exploitation pour vous verser un revenu passif mensuel. Offre sous 48h." />
      </Helmet>

      <MainHeader variant="sortie" onOpenForm={onOpenForm} />
      
      {/* Hero */}
      <section className="min-h-[90vh] flex items-center relative pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-sm text-red-400 mb-8">
              <Battery className="w-4 h-4" />
              Sortie opérationnelle
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight max-w-4xl" data-testid="sortie-h1">
              Vous êtes <span className="text-red-400">prisonnier</span> de votre propre conciergerie ?
            </h1>

            <p className="text-xl text-[#A1A1AA] mb-10 max-w-2xl leading-relaxed">
              Check-in à 23h. Messages à gérer le dimanche. Équipes qui vous lâchent. Vous n'avez pas créé une entreprise, vous avez créé un job qui vous épuise.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={onOpenForm}
                className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold px-8 py-4 text-lg rounded-lg inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="sortie-cta"
              >
                Je veux en sortir
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={() => window.open(BOOKING_URL, '_blank')}
                className="border border-white/20 text-white hover:bg-white/5 px-8 py-4 text-lg rounded-lg inline-flex items-center justify-center transition-colors"
              >
                Parler à un expert
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-red-400 text-sm tracking-[0.15em] uppercase font-medium">La réalité</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Vous vous reconnaissez ?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: Clock, text: "Vous travaillez 7j/7 sans jamais décrocher" },
              { icon: AlertCircle, text: "Chaque notification vous stresse" },
              { icon: Users, text: "Vos équipes changent tout le temps" },
              { icon: TrendingDown, text: "Votre rentabilité fond avec les imprévus" },
              { icon: Battery, text: "Vous êtes épuisé physiquement et mentalement" },
              { icon: Target, text: "Vous ne savez plus pourquoi vous faites ça" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4 p-5 bg-[#111111] rounded-xl border border-white/5"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-red-400" />
                </div>
                <p className="text-white text-lg">{item.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 p-6 bg-[#111111] border-l-4 border-[#FF4500] rounded-r-xl"
          >
            <p className="text-white text-lg">
              <strong>Ce n'est pas un échec.</strong> C'est un modèle qui a atteint ses limites. La vraie question : comment en sortir sans tout perdre ?
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 px-4 bg-[#050505]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#FF4500] text-sm tracking-[0.15em] uppercase font-medium">Les solutions</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-4">
              2 façons de sortir de l'opérationnel
            </h2>
            <p className="text-[#A1A1AA] text-lg">
              Selon votre situation, une option sera plus adaptée.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { 
                icon: DollarSign, 
                title: "Rachat structuré", 
                desc: "On rachète votre conciergerie. Paiement structuré : cash + earn-out. Vous passez à autre chose.",
                highlight: "Sortie complète"
              },
              { 
                icon: PiggyBank, 
                title: "Revenu passif", 
                desc: "Vos contrats passent chez nous. On gère tout. Vous touchez une commission récurrente basée sur la performance.",
                highlight: "Revenus récurrents"
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="p-6 bg-[#0A0A0A] border border-white/10 rounded-xl hover:border-[#FF4500]/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FF4500]/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#FF4500]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] mb-4">{item.desc}</p>
                <span className="inline-block px-3 py-1 rounded-full bg-[#FF4500]/10 text-[#FF4500] text-sm font-medium">
                  {item.highlight}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Projection */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Dans 3 mois, vous pourriez...
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              "Ne plus gérer un seul check-in",
              "Profiter de vos week-ends",
              "Recevoir un virement sans effort",
              "Dormir sans stress",
              "Avoir du temps pour d'autres projets",
              "Retrouver une vie normale"
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 p-4 bg-[#111111] rounded-xl"
              >
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-white">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <AuthorityBlock onOpenForm={onOpenForm} />

      {/* CTA */}
      <section id="diagnostic" className="py-20 px-4 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FF4500]/5 blur-3xl" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à reprendre votre vie ?
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-8">
            Diagnostic gratuit en 3 minutes. Sans engagement.
          </p>
          <button 
            onClick={onOpenForm}
            className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold px-10 py-5 text-lg rounded-lg animate-pulse inline-flex items-center justify-center gap-2 transition-colors"
            data-testid="sortie-final-cta"
          >
            Démarrer mon diagnostic gratuit
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <MainFooter />
      <SocialProofBar />
      <StickyCTAMobile onOpenForm={onOpenForm} />
    </div>
  );
};

// ============ LANDING 2: RACHAT / REVENU PASSIF ============
export const ValorisationVentePage = ({ onOpenForm }) => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Rachat conciergerie Airbnb | Offre sous 48h | Cledici Exit</title>
        <meta name="description" content="Nous rachetons votre conciergerie Airbnb. Paiement structuré (cash + earn-out) ou revenu passif basé sur la performance. Soumettez votre dossier, offre sous 48h." />
      </Helmet>

      <MainHeader variant="valorisation" onOpenForm={onOpenForm} />
      
      {/* Hero */}
      <section className="min-h-[90vh] flex items-center relative pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/10 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-sm text-green-400 mb-8">
              <TrendingUp className="w-4 h-4" />
              Rachat & Revenu Passif
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight max-w-4xl" data-testid="valorisation-h1">
              Nous rachetons <span className="text-green-400">votre conciergerie</span>
            </h1>

            <p className="text-xl text-[#A1A1AA] mb-10 max-w-2xl leading-relaxed">
              Votre activité fonctionne ? Nous sommes intéressés. Soumettez votre dossier, on l'étudie et on vous fait une offre. Simple et rapide.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={onOpenForm}
                className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold px-8 py-4 text-lg rounded-lg inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="valorisation-cta"
              >
                Soumettre mon dossier
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={() => window.open(BOOKING_URL, '_blank')}
                className="border border-white/20 text-white hover:bg-white/5 px-8 py-4 text-lg rounded-lg inline-flex items-center justify-center transition-colors"
              >
                Discuter de mon projet
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Two Options */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-green-400 text-sm tracking-[0.15em] uppercase font-medium">Deux possibilités</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Choisissez votre sortie
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Option 1: Rachat */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 bg-[#111111] border border-white/10 rounded-2xl"
            >
              <div className="w-14 h-14 rounded-xl bg-[#FF4500]/10 flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-[#FF4500]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Rachat de votre conciergerie
              </h3>
              <p className="text-[#A1A1AA] mb-6 leading-relaxed">
                On étudie votre dossier et on vous fait une offre de rachat. Si ça matche, vous vendez et vous passez à autre chose.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Offre sous 48h après étude",
                  "Transaction sécurisée",
                  "Transition accompagnée",
                  "Paiement structuré selon le deal"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#A1A1AA]">
                    <Check className="w-4 h-4 text-[#FF4500] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-white/10">
                <p className="text-[#71717A] text-sm">Idéal si vous voulez tourner la page rapidement.</p>
              </div>
            </motion.div>

            {/* Option 2: Revenu Passif - Highlighted */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 bg-gradient-to-br from-green-900/20 to-[#111111] border-2 border-green-500/30 rounded-2xl relative"
            >
              <div className="absolute -top-3 right-6 px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-full">
                RECOMMANDÉ
              </div>
              <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Revenu passif à vie
              </h3>
              <p className="text-[#A1A1AA] mb-6 leading-relaxed">
                Plutôt qu'un rachat sec, transformez votre parc en source de revenus récurrents. On reprend la gestion, vous touchez chaque mois.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Commissions mensuelles récurrentes",
                  "Basées sur la performance du parc",
                  "Bonus sur les nouveaux clients que vous amenez",
                  "Zéro gestion, zéro stress"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-green-500/20">
                <p className="text-green-400 text-sm font-medium">Plus intelligent qu'un simple rachat.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Passive Income is Better */}
      <section className="py-20 px-4 bg-[#050505]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-[#FF4500] text-sm tracking-[0.15em] uppercase font-medium">Pourquoi le revenu passif</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              L'option que les malins choisissent
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="p-5 bg-[#0A0A0A] border border-white/10 rounded-xl">
                <h4 className="text-white font-semibold mb-2">Rachat classique</h4>
                <p className="text-[#A1A1AA]">Vous vendez une fois → vous touchez une fois → c'est fini.</p>
              </div>
              <div className="p-5 bg-green-500/5 border border-green-500/20 rounded-xl">
                <h4 className="text-green-400 font-semibold mb-2">Revenu passif Cledici</h4>
                <p className="text-white">Vos contrats passent chez nous → vous touchez chaque mois.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {[
                { title: "Zéro gestion", desc: "On reprend tout l'opérationnel à 100%." },
                { title: "Revenus récurrents", desc: "Commission mensuelle basée sur la performance du parc." },
                { title: "Bonus apporteur", desc: "Vous amenez un nouveau proprio ? Vous touchez dessus aussi." },
                { title: "Liberté totale", desc: "Plus de charge mentale, plus de téléphone qui sonne." }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">{item.title}</h4>
                    <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Comment ça marche
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { num: "01", title: "Vous soumettez", desc: "Remplissez le diagnostic en 3 minutes" },
              { num: "02", title: "On étudie", desc: "Analyse de votre portefeuille sous 48h" },
              { num: "03", title: "On propose", desc: "Offre de rachat OU revenu passif" },
              { num: "04", title: "Vous décidez", desc: "Vous choisissez ce qui vous convient" }
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl font-bold text-[#FF4500]/20 mb-4">{step.num}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Criteria */}
      <section className="py-20 px-4 bg-[#050505]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-green-400 text-sm tracking-[0.15em] uppercase font-medium">Critères</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Ce qui nous intéresse
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Minimum 5 logements sous gestion",
              "Activité rentable depuis 1+ an",
              "Mandats exclusifs de préférence",
              "Process documentés (même basiques)",
              "Propriétaires satisfaits",
              "France métropolitaine"
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-white/10 rounded-xl"
              >
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-white">{item}</p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-[#71717A] text-center mt-8"
          >
            Vous ne cochez pas tout ? Soumettez quand même. On étudie chaque dossier.
          </motion.p>
        </div>
      </section>

      <AuthorityBlock onOpenForm={onOpenForm} />

      {/* CTA */}
      <section id="diagnostic" className="py-20 px-4 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-green-500/5 blur-3xl" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            On rachète ou on vous verse des commissions à vie
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-8">
            Soumettez votre dossier. On revient vers vous sous 48h avec une proposition.
          </p>
          <button 
            onClick={onOpenForm}
            className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold px-10 py-5 text-lg rounded-lg inline-flex items-center justify-center gap-2 transition-colors"
            data-testid="valorisation-final-cta"
          >
            Soumettre mon dossier
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <MainFooter />
      <SocialProofBar />
      <StickyCTAMobile onOpenForm={onOpenForm} />
    </div>
  );
};
